import { memo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ArrowRight, Pencil } from 'lucide-react';
import { Scenario } from 'types';

interface FlipCardProps {
  scenario: Scenario;
  onOpen: (id: string) => void;
  onEdit?: (id: string) => void;
  roleLabelOverride?: string;
}

// Ховер-флип на чистом CSS (без useState) + memo: после монтирования карточка
// не ререндерится при наборе в поиске и при наведении — стили emotion не пере-сериализуются.
function FlipCard({ scenario, onOpen, onEdit, roleLabelOverride }: FlipCardProps) {
  return (
    <Box
      sx={{
        height: 228,
        position: 'relative',
        cursor: 'pointer',
        '& .fc-front': { opacity: 1, pointerEvents: 'auto', transition: 'opacity 0.22s ease' },
        '& .fc-back': { opacity: 0, pointerEvents: 'none', transition: 'opacity 0.22s ease' },
        '&:hover .fc-front': { opacity: 0, pointerEvents: 'none' },
        '&:hover .fc-back': { opacity: 1, pointerEvents: 'auto' },
        '& .fc-edit': {
          color: 'var(--color-text-muted)',
          bgcolor: 'rgba(255,255,255,0.7)',
          borderColor: 'rgba(20,116,255,0.2)',
        },
        '&:hover .fc-edit': {
          color: 'var(--color-primary)',
          bgcolor: 'rgba(255,255,255,0.95)',
          borderColor: 'rgba(255,255,255,0.4)',
        },
      }}
    >
      {/* ── FRONT ── */}
      <Box
        className="fc-front"
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          borderRadius: 'var(--radius-lg)',
          background: '#FFFFFF',
          border: '1px solid rgba(20,116,255,0.12)',
          boxShadow: '0 4px 20px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.04)',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {roleLabelOverride && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: onEdit ? 36 : 0,
                px: 1,
                py: 0.25,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                bgcolor: 'rgba(20,116,255,0.08)',
                border: '1px solid rgba(20,116,255,0.2)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-primary)',
                zIndex: 1,
              }}
            >
              {roleLabelOverride}
            </Box>
          )}
          {/* Number badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.25,
              py: 0.375,
              background: 'rgba(20,116,255,0.08)',
              border: '1px solid rgba(20,116,255,0.16)',
              borderRadius: 'var(--radius-sm)',
              mb: 1.75,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                color: 'var(--color-primary)',
                letterSpacing: '0.06em',
              }}
            >
              {scenario.number}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              lineHeight: 1.35,
              mb: 0.875,
              letterSpacing: '-0.2px',
            }}
          >
            {scenario.title}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
            {scenario.shortDescription}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 11,
              color: 'var(--color-text-placeholder)',
              letterSpacing: '0.01em',
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                bgcolor: 'rgba(20,116,255,0.22)',
                flexShrink: 0,
              }}
            />
            наведите чтобы узнать больше
          </Box>
        </Box>
      </Box>

      {/* ── BACK ── */}
      <Box
        className="fc-back"
        onClick={() => onOpen(scenario.id)}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          borderRadius: 'var(--radius-lg)',
          background: 'linear-gradient(150deg, #0A45C0 0%, #1474FF 45%, #3B8BFF 100%)',
          boxShadow: '0 12px 40px rgba(20,116,255,0.35), inset 0 1px 0 rgba(255,255,255,0.20)',
          display: 'flex',
          flexDirection: 'column',
          color: '#fff',
          overflow: 'hidden',
        }}
      >
        {/* Subtle inner glow */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        {/* Steps */}
        <Box
          sx={{
            px: 2.5,
            pt: 2.25,
            pb: 0,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1.25,
              mb: 1.25,
              pr: onEdit ? 4 : 0,
              flexShrink: 0,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {scenario.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              opacity: 0.6,
              mb: 1,
              flexShrink: 0,
            }}
          >
            Что делает
          </Typography>
          <Box
            component="ul"
            sx={{
              p: 0,
              m: 0,
              pr: 0.5,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.4) transparent',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255,255,255,0.35)',
                borderRadius: 2,
              },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
            }}
          >
            {scenario.steps.map((step, i) => (
              <Box
                key={i}
                component="li"
                sx={{
                  fontSize: 11.5,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1,
                  lineHeight: 1.4,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    fontWeight: 700,
                    fontSize: 10,
                    fontVariantNumeric: 'tabular-nums',
                    width: 16,
                    color: 'rgba(255,255,255,0.75)',
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </Box>
                <Box component="span" sx={{ opacity: 0.92 }}>
                  {step.label}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ mx: 2.5, my: 1.5, height: '1px', bgcolor: 'rgba(255,255,255,0.18)' }} />

        {/* Result + CTA */}
        <Box sx={{ px: 2.5, pb: 2.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                opacity: 0.6,
                flexShrink: 0,
                lineHeight: 1.6,
              }}
            >
              Итог
            </Typography>
            <Typography sx={{ fontSize: 11.5, fontWeight: 500, opacity: 0.92, lineHeight: 1.4 }}>
              {scenario.result}
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.16)',
              border: '1px solid rgba(255,255,255,0.28)',
              borderRadius: 'var(--radius-sm)',
              py: 0.875,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.625,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'background 0.15s, border-color 0.15s',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.26)',
                borderColor: 'rgba(255,255,255,0.42)',
              },
            }}
          >
            Запустить сценарий
            <ArrowRight size={12} />
          </Box>
        </Box>
      </Box>

      {onEdit && (
        <IconButton
          className="fc-edit"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(scenario.id);
          }}
          aria-label="Редактировать сценарий"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 3,
            pointerEvents: 'auto',
            border: '1px solid',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            p: 0.75,
            '&:hover': {
              color: 'var(--color-primary)',
              bgcolor: 'rgba(255,255,255,1)',
              borderColor: 'var(--color-primary)',
            },
          }}
        >
          <Pencil size={14} />
        </IconButton>
      )}
    </Box>
  );
}

export default memo(FlipCard);
